// Tree weights as one allocation of 100, dragged at the boundaries.
//
// Three number inputs, or three sliders, cannot express this: the values have to
// total 100, so moving one has to move at least one other, and nothing in the
// control says which. Adjusting a slider and watching two other numbers change
// by amounts that depend on their current ratio is not a control, it is a
// negotiation.
//
// A divider is unambiguous. Divider k trades tree k against tree k+1 and nothing
// else moves, so the total is 100 by construction rather than by validation. The
// old form had a validation error for a total that was not 100, which this makes
// unreachable.
//
// Two failures found in the prototype and fixed here, both at the extremes. At
// 100/0/0 the bar was one full width segment and both dividers sat on top of each
// other at the same edge, so neither the second tree nor the second handle
// existed as far as the user could tell.
//
// A zero weight therefore keeps a fixed sliver, hatched rather than solid because
// at that width the bar is no longer proportional and should not pretend to be.
// Those pixels are RESERVED rather than imposed as a `min-width`: a floor on a
// flex item is invisible to the percentage the dividers are positioned by, so the
// bands and the handles drifted apart by exactly the pixels the floor added, and
// at 100/0/0 each handle sat 76px from the boundary it controlled. `SLIVER_PX` is
// subtracted from the track before any share is applied, and every band, every
// divider and the pointer arithmetic read that one scale.
//
// Reserving also makes coincident dividers impossible, since two of them can only
// meet across a band of weight zero and such a band is now `SLIVER_PX` wide. The
// nudge-and-ring that used to separate them is gone with the case it handled.
import React from 'react';
import { observer } from 'mobx-react';

import './weightAllocationBar.css';

/**
 * Weights move in whole tens, which is the grid the engine's own auto select
 * searches: `weights_combinations.json` is every allocation of 100 across three
 * trees in steps of ten, all 66 of them. A finer handle would offer precision
 * the rest of the system does not use.
 */
const STEP = 10;

// Three tints of the Very Good blue, dark to light, so the first tree reads as
// the heaviest by default. Precomputed against the `#dbe3ea` hairline rather
// than left to `color-mix`, to match how every other colour in this work is
// written.
const TINTS = ['#1e77cc', '#66a0d7', '#9bbee0'];

const tintOf = (index: number) => TINTS[index % TINTS.length];

/**
 * What a tree carrying no weight is given, in pixels.
 *
 * Wide enough to be a target and to read as a band rather than a border. Taken
 * out of the track before shares are applied, never added afterwards.
 */
const SLIVER_PX = 38;

/**
 * The allocation after dragging divider `index` to `percent`.
 *
 * Only the two trees the divider sits between change, and their sum is
 * preserved, so the total stays 100 without a correction pass. The target is
 * clamped into the span those two occupy: dragging past a neighbour would
 * otherwise push a third tree, which is the behaviour this control exists to
 * remove.
 */
export function moveDivider(
  values: number[],
  index: number,
  percent: number
): number[] {
  const snapped = Math.max(0, Math.min(100, Math.round(percent / STEP) * STEP));
  const below = values.slice(0, index).reduce((sum, value) => sum + value, 0);
  const pair = values[index] + values[index + 1];
  const target = Math.max(below, Math.min(below + pair, snapped));

  const next = values.slice();
  next[index] = target - below;
  next[index + 1] = pair - next[index];
  return next;
}

function WeightAllocationBar({
  weights,
  onChange,
  disabled,
}: {
  /** Tree id to weight, as a fraction of 1, which is how the model stores it. */
  weights: Record<string, number>;
  /** Called with the same shape, only on a real drag or key press. */
  onChange: (weights: Record<string, number>) => void;
  disabled?: boolean;
}) {
  const barRef = React.useRef<HTMLDivElement>(null);
  const dividerRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const trees = Object.keys(weights);
  // Exact percentages for geometry. The labels round, the bar does not: a stored
  // 33.33 would otherwise draw three segments totalling 99 and leave a gap.
  const values = trees.map((tree) => Number(weights[tree]) * 100);

  const apply = (index: number, percent: number) => {
    const next = moveDivider(values, index, percent);
    onChange(
      trees.reduce<Record<string, number>>((out, tree, position) => {
        out[tree] = next[position] / 100;
        return out;
      }, {})
    );
  };

  // The track left for the shares once every zero band has been paid for. Bands
  // and dividers express it as a calc so it stays correct through a resize
  // without measuring anything.
  const reservedPx = values.filter((value) => value === 0).length * SLIVER_PX;
  const track = `(100% - ${reservedPx}px)`;

  /** Where a share sits along the bar, as CSS, on the one shared scale. */
  const offsetOf = (share: number, zerosBefore: number) =>
    `calc(${track} * ${share / 100} + ${zerosBefore * SLIVER_PX}px)`;

  /**
   * The weight the pointer is over.
   *
   * Walks the bands rather than dividing by the width, because the reserved
   * pixels are interleaved with the shares and not all at one end. Landing
   * inside a zero band returns that band's own edge, which is the only weight it
   * occupies however many pixels it is drawn with.
   */
  const percentAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect?.width) return 0;
    const proportional = Math.max(0, rect.width - reservedPx);
    if (!proportional) return 0;

    const x = clientX - rect.left;
    let drawn = 0;
    let weight = 0;
    for (let index = 0; index < values.length; index += 1) {
      const value = values[index];
      const width = value === 0 ? SLIVER_PX : (value / 100) * proportional;
      if (x <= drawn + width) {
        if (value === 0) return weight;
        return weight + ((x - drawn) / proportional) * 100;
      }
      drawn += width;
      weight += value;
    }
    return 100;
  };

  // Cumulative edge each divider sits on, in weight and in reserved slivers.
  const edges = values.slice(0, -1).map((value, index) => ({
    share: values.slice(0, index + 1).reduce((sum, each) => sum + each, 0),
    zerosBefore: values.slice(0, index + 1).filter((each) => each === 0).length,
  }));

  return (
    <div className="wbar-wrap">
      <div className="wbar" ref={barRef}>
        {values.map((value, index) => (
          <div
            key={trees[index]}
            className={`wbar-seg ${value === 0 ? 'wbar-seg--zero' : ''}`}
            style={{
              width:
                value === 0
                  ? `${SLIVER_PX}px`
                  : `calc(${track} * ${value / 100})`,
              background: value === 0 ? undefined : tintOf(index),
            }}
            title={
              value === 0
                ? `Tree ${trees[index]} carries no weight, shown as a sliver so it stays reachable`
                : `Tree ${trees[index]}: ${Math.round(
                    value
                  )}% of the blended score`
            }
          >
            {/* The legend below the bar is gone, so the bar is now the only
                place a tree is named. It cannot drop the label of a narrow
                segment any more: what used to be covered by a chip is covered
                by shrinking. Below the width that fits "Tree 1" the name goes
                and the number stays, and a tree at zero keeps its reserved
                sliver, so no tree can vanish from the control. */}
            {value >= 14 && (
              <span className="wbar-seg-name">Tree {trees[index]}</span>
            )}
            <span className="wbar-seg-value">{Math.round(value)}</span>
          </div>
        ))}
      </div>

      {edges.map((edge, index) => {
        // Whichever divider has room to move sits on top. Two can no longer
        // land on the same pixel, since the only way to meet is across a band
        // of weight zero and that band is now a reserved sliver wide, but the
        // ordering still decides which one a pointer between them reaches.
        const room = values[index] + values[index + 1];
        const otherRoom =
          index === 0
            ? (values[1] ?? 0) + (values[2] ?? 0)
            : (values[index - 1] ?? 0) + values[index];
        return (
          <button
            type="button"
            key={`divider_${index}`}
            ref={(element) => {
              dividerRefs.current[index] = element;
            }}
            className="wbar-divider"
            style={{
              left: offsetOf(edge.share, edge.zerosBefore),
              zIndex: room >= otherRoom ? 3 : 2,
            }}
            role="slider"
            aria-label={`Trade weight between tree ${trees[index]} and tree ${
              trees[index + 1]
            }`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(edge.share)}
            aria-valuetext={`Tree ${trees[index]} ${Math.round(
              values[index]
            )}, tree ${trees[index + 1]} ${Math.round(values[index + 1])}`}
            disabled={disabled}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
              if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
                return;
              }
              apply(index, percentAt(event.clientX));
            }}
            onPointerUp={(event) => {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            onKeyDown={(event) => {
              const delta =
                event.key === 'ArrowLeft'
                  ? -STEP
                  : event.key === 'ArrowRight'
                  ? STEP
                  : 0;
              if (!delta) return;
              event.preventDefault();
              apply(index, edge.share + delta);
            }}
          />
        );
      })}
    </div>
  );
}

export default observer(WeightAllocationBar);
